'use client';
import { Forward10, Pause, PlayArrow, Replay10 } from '@mui/icons-material';
import { Box, Card, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { type MouseEvent, type TouchEvent, useEffect, useRef, useState } from 'react';

export default function AudioPlayer({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      setProgress((audio.currentTime / audio.duration) * 100);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      audio.currentTime = 0;
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSkip = (seconds: number) => {
    if (!audioRef.current) return;
    const newTime = audioRef.current.currentTime + seconds;
    audioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (event: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>) => {
    if (!audioRef.current) return;

    const bounds = event.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in event ? event.touches[0].clientX : (event as MouseEvent).clientX;

    const x = clientX - bounds.left;
    const clickedValue = (x / bounds.width) * 100;
    const newTime = (clickedValue / 100) * duration;

    audioRef.current.currentTime = Math.max(0, Math.min(newTime, duration));
    setProgress(clickedValue);
  };

  return (
    <Card
      sx={{
        p: { xs: 1, sm: 2 },
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, sm: 2 },
        width: '100%',
      }}
      variant="outlined">
      <audio ref={audioRef} src={url}>
        <track kind="captions" />
      </audio>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
        <Tooltip title="10 Sekunden zurück">
          <IconButton onClick={() => handleSkip(-10)} size="small" sx={{ color: 'primary.main' }}>
            <Replay10 />
          </IconButton>
        </Tooltip>

        <Tooltip title={isPlaying ? 'Pause' : 'Abspielen'}>
          <IconButton
            onClick={togglePlay}
            size="medium"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            }}>
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
        </Tooltip>

        <Tooltip title="10 Sekunden vor">
          <IconButton onClick={() => handleSkip(10)} size="small" sx={{ color: 'primary.main' }}>
            <Forward10 />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            minWidth: '35px',
            userSelect: 'none',
          }}>
          {formatTime(duration * (progress / 100))}
        </Typography>

        <Box
          onClick={handleProgressClick}
          onTouchStart={handleProgressClick}
          sx={{
            cursor: 'pointer',
            flex: 1,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            touchAction: 'none',
          }}>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              width: '100%',
              height: 4,
              borderRadius: 2,
              cursor: 'pointer',
              '& .MuiLinearProgress-bar': {
                transition: 'none',
              },
            }}
          />
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            minWidth: '35px',
            userSelect: 'none',
          }}>
          {formatTime(duration)}
        </Typography>
      </Box>
    </Card>
  );
}
